import { request } from './api'

const adminApiBaseUrl = process.env.VUE_APP_ADMIN_API_URL || '/api/admin-api'

export interface AdminStats {
  totalUsers: number
  totalConversations: number
  totalMessages: number
  todayNewUsers: number
  totalPointsDistributed: number
}

export interface AdminUser {
  id: string
  username?: string
  email?: string
  nickname?: string
  avatar_url?: string
  points: number
  is_admin: boolean
  role: 'user' | 'admin'
  is_disabled: boolean
  created_at: string
  updated_at: string
}

export interface PaginatedUsers {
  list: AdminUser[]
  total: number
  page: number
  pageSize: number
}

export interface AdminTransaction {
  id: string
  user_id: string
  username?: string
  email?: string
  nickname?: string
  amount: number
  type: string
  reason?: string
  balance_after: number
  created_at: string
}

export interface PaginatedTransactions {
  list: AdminTransaction[]
  total: number
  page: number
  pageSize: number
}

export interface DateRangeFilter {
  startDate?: string
  endDate?: string
}

export type FeedbackStatus = 'pending' | 'processing' | 'resolved' | 'closed'
export type FeedbackType = 'bug' | 'feature' | 'question' | 'other'

export interface FeedbackReply {
  id: string
  feedback_id: string
  author_id?: string
  author_role?: 'user' | 'admin'
  content: string
  is_admin_reply: boolean
  created_at: string
}

export interface Feedback {
  id: string
  user_id: string
  username?: string
  nickname?: string
  email?: string
  type: FeedbackType
  title: string
  content: string
  status: FeedbackStatus
  created_at: string
  updated_at: string
  replies?: FeedbackReply[]
  first_response_at?: string | null
}

export interface PaginatedFeedbacks {
  list: Feedback[]
  total: number
  page: number
  pageSize: number
}

export interface CostBenefitAnalytics {
  totalUsers: number
  activeUsers: number
  totalConversations: number
  totalRechargePoints: number
  totalCostPoints: number
  tokenCostPoints: number
  totalCostWithToken: number
  totalTokenIn: number
  totalTokenOut: number
  arpuPoints: number
  costPerConversation: number
  profitMargin: number
}

export interface PptWorker {
  id: string
  name: string
  provider: 'official' | 'third_party'
  supported_task_kinds: string[]
  enabled: boolean
  priority: number
  weight: number
  max_concurrency: number
  current_task_count: number
  last_heartbeat_at?: string | null
}

export interface FunnelAnalytics {
  stages: { registered: number; activated: number; firstExport: number; returned: number }
  conversionRates: { registeredToActivated: number; activatedToFirstExport: number; firstExportToReturned: number; overall: number }
}

export interface FrequencyAnalytics {
  wau: number
  mau: number
  stickiness: number
  avgTasksPerWeek: number
  secondTaskRate7Day: number
  secondTaskRate30Day: number
  firstExportUsers: number
  returned7Day: number
  returned30Day: number
  totalRegistered: number
}

export interface TaskOutcomeAnalytics {
  overall: { completed: number; failed: number; cancelled: number; expired: number; other: number; total: number; successRate: number; failRate: number; cancelRate: number }
  byMode: Record<string, { completed: number; failed: number; cancelled: number; expired: number; other: number; total: number; successRate: number; failRate: number; cancelRate: number }>
}

export interface PageReworkAnalytics {
  totalPages: number
  reworkedPages: number
  reworkRate: number
  totalTasks: number
  tasksWithRework: number
  taskReworkRate: number
}

export interface CostAnalytics {
  actualCostAvailable: boolean
  retailValueYuan: number
  netChargedPages: number
  retailYuanPerPage: number
  completedTasks: number
  failedTasks: number
  byMode: Record<string, number>
  topUsersByCost: Array<{ user_id: string; cost_yuan: number }>
}

export interface WillingnessAnalytics {
  totalRegistered: number
  exhaustedUsers: number
  exhaustionRate: number
  uniqueClickers: number
  totalClicks: number
  clickThroughRate: number
}

export interface OverviewTrendPoint {
  date: string
  registeredUsers: number
  activeUsers: number
  tasks: number
  completedTasks: number
  failedTasks: number
  netChargedPages: number
  retailValueYuan: number
}

export interface OverviewTrendAnalytics {
  startDate: string
  endDate: string
  points: OverviewTrendPoint[]
}

export interface AdminTask {
  id: string
  user_id: string
  creator_email?: string | null
  creator_nickname?: string | null
  mode: string
  status: string
  title: string
  total_pages: number
  charged_pages: number
  reserved_pages: number
  refunded_pages: number
  net_charged_pages: number
  page_status_counts: Record<string, number>
  retail_value_yuan: number
  cost_points: number
  current_stage: string
  progress: number
  worker_attempts?: number
  max_worker_attempts?: number
  error_public: string | null
  error_admin: string | null
  pptx_url?: string | null
  quality_threshold_version?: number | null
  quality_report_url?: string | null
  validation_url?: string | null
  preview_url?: string | null
  contact_sheet_url?: string | null
  performance_trace_url?: string | null
  agent_timings_url?: string | null
  quality_failure_summary?: Array<{ page_index: number; reason: string }>
  diagnostic_artifacts?: Array<{ type: string; kind: string; url?: string | null; storage_path?: string | null }>
  session_metrics?: {
    agent_model?: string
    token_usage?: {
      input_tokens?: number
      cached_input_tokens?: number
      output_tokens?: number
      reasoning_output_tokens?: number
    }
    runs?: Array<{ run_id: string; image_model?: string | null; page_count?: number; image_calls?: number; page_assets?: Record<string, number> }>
    total_asset_files?: number
    generated_images?: number
    image_calls?: number
  } | null
  created_at: string
  updated_at: string
  completed_at: string | null
}

export interface PaginatedAdminTasks {
  list: AdminTask[]
  total: number
  page: number
  pageSize: number
}


export interface QualityThresholdVersion {
  id: string
  version: number
  thresholds: Record<string, number | string | boolean>
  note: string
  created_by?: string | null
  created_at: string
}

export interface QualityThresholdsResponse {
  current: QualityThresholdVersion
  history: QualityThresholdVersion[]
}

export interface UpdateQualityThresholdRequest {
  thresholds: Record<string, number | string | boolean>
  note?: string
}

export interface AdminTaskFilters {
  startDate?: string
  endDate?: string
  title?: string
  creator?: string
  status?: string
  mode?: string
}

export interface UserSearchFilters {
  q?: string
  pointsMin?: number
  pointsMax?: number
  createdAfter?: string
}

const feedbackStatusMap: Record<FeedbackStatus, string> = {
  pending: '待处理',
  processing: '处理中',
  resolved: '已解决',
  closed: '已关闭',
}

const feedbackTypeMap: Record<FeedbackType, string> = {
  bug: 'BUG',
  feature: '功能建议',
  question: '使用问题',
  other: '其他',
}

export function formatFeedbackStatus(status: FeedbackStatus): string {
  return feedbackStatusMap[status] || status
}

export function formatFeedbackType(type: FeedbackType): string {
  return feedbackTypeMap[type] || type
}

// ---------------------------------------------------------------------------
// Admin API helpers — use request() with admin endpoint prefix
// ---------------------------------------------------------------------------

function adminRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  return request<T>(`${adminApiBaseUrl}${path}`, options)
}

function adminGet<T>(path: string): Promise<T> {
  return adminRequest<T>(path)
}

function adminPost<T>(path: string, body?: unknown): Promise<T> {
  return adminRequest<T>(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  })
}

function appendDateRange(params: URLSearchParams, range?: DateRangeFilter): void {
  if (range?.startDate) params.set('startDate', range.startDate)
  if (range?.endDate) params.set('endDate', range.endDate)
}

function dateRangeQuery(range?: DateRangeFilter): string {
  const params = new URLSearchParams()
  appendDateRange(params, range)
  const query = params.toString()
  return query ? `?${query}` : ''
}

// ---------------------------------------------------------------------------
// Exported API functions
// ---------------------------------------------------------------------------

export function getAdminStats(range?: DateRangeFilter): Promise<AdminStats> {
  return adminGet<AdminStats>(`/stats${dateRangeQuery(range)}`)
}

export function listUsers(page = 1, pageSize = 20, filters?: UserSearchFilters): Promise<PaginatedUsers> {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
  if (filters?.q) params.set('q', filters.q)
  if (filters?.pointsMin !== undefined) params.set('points_min', String(filters.pointsMin))
  if (filters?.pointsMax !== undefined) params.set('points_max', String(filters.pointsMax))
  if (filters?.createdAfter) params.set('created_after', filters.createdAfter)
  return adminGet<PaginatedUsers>(`/users?${params.toString()}`)
}

export async function updateUserRole(userId: string, role: 'user' | 'admin'): Promise<void> {
  await adminPost(`/users/${userId}/role`, { role })
}

export function allocatePoints(
  userId: string,
  amount: number,
  reason: string,
  operationKey: string,
): Promise<{ balance: number }> {
  return adminPost<{ balance: number }>(`/users/${userId}/points`, { amount, reason, operationKey })
}

export function deductPoints(
  userId: string,
  amount: number,
  reason: string,
  operationKey: string,
): Promise<{ balance: number }> {
  return adminPost<{ balance: number }>(`/users/${userId}/points/deduct`, { amount, reason, operationKey })
}

export async function setUserDisabled(userId: string, disabled: boolean): Promise<void> {
  await adminPost(`/users/${userId}/disable`, { disabled })
}

export function batchDisableUsers(
  userIds: string[],
  disabled: boolean,
): Promise<{ updated: number; skipped_admins: number }> {
  return adminPost<{ updated: number; skipped_admins: number }>(`/users/batch-disable`, { userIds, disabled })
}

export function listTransactions(page = 1, pageSize = 20, range?: DateRangeFilter): Promise<PaginatedTransactions> {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
  appendDateRange(params, range)
  return adminGet<PaginatedTransactions>(`/transactions?${params.toString()}`)
}

export function listFeedbacks(page = 1, pageSize = 20): Promise<PaginatedFeedbacks> {
  return adminGet<PaginatedFeedbacks>(`/admin/feedbacks?page=${page}&page_size=${pageSize}`)
}

export async function replyFeedback(feedbackId: string, content: string): Promise<void> {
  await adminPost(`/admin/feedbacks/${feedbackId}/reply`, { content })
}

export async function updateFeedbackStatus(feedbackId: string, status: FeedbackStatus): Promise<void> {
  await adminPost(`/admin/feedbacks/${feedbackId}/status`, { status })
}

export function getCostBenefitAnalytics(range?: DateRangeFilter): Promise<CostBenefitAnalytics> {
  return adminGet<CostBenefitAnalytics>(`/analytics/cost-benefit${dateRangeQuery(range)}`)
}

export function getFunnelAnalytics(range?: DateRangeFilter): Promise<FunnelAnalytics> {
  return adminGet<FunnelAnalytics>(`/analytics/funnel${dateRangeQuery(range)}`)
}

export function getFrequencyAnalytics(range?: DateRangeFilter): Promise<FrequencyAnalytics> {
  return adminGet<FrequencyAnalytics>(`/analytics/frequency${dateRangeQuery(range)}`)
}

export function getTaskOutcomeAnalytics(range?: DateRangeFilter): Promise<TaskOutcomeAnalytics> {
  return adminGet<TaskOutcomeAnalytics>(`/analytics/task-outcomes${dateRangeQuery(range)}`)
}

export function getPageReworkAnalytics(range?: DateRangeFilter): Promise<PageReworkAnalytics> {
  return adminGet<PageReworkAnalytics>(`/analytics/page-rework${dateRangeQuery(range)}`)
}

export function getCostAnalytics(range?: DateRangeFilter): Promise<CostAnalytics> {
  return adminGet<CostAnalytics>(`/analytics/cost${dateRangeQuery(range)}`)
}

export function getWillingnessAnalytics(range?: DateRangeFilter): Promise<WillingnessAnalytics> {
  return adminGet<WillingnessAnalytics>(`/analytics/willingness${dateRangeQuery(range)}`)
}

export function getOverviewTrends(range?: DateRangeFilter): Promise<OverviewTrendAnalytics> {
  return adminGet<OverviewTrendAnalytics>(`/analytics/overview-trends${dateRangeQuery(range)}`)
}

export function listAdminTasks(page = 1, pageSize = 20, filters?: AdminTaskFilters): Promise<PaginatedAdminTasks> {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
  if (filters?.startDate) params.set('startDate', filters.startDate)
  if (filters?.endDate) params.set('endDate', filters.endDate)
  if (filters?.title) params.set('title', filters.title)
  if (filters?.creator) params.set('creator', filters.creator)
  if (filters?.status) params.set('status', filters.status)
  if (filters?.mode) params.set('mode', filters.mode)
  return adminGet<PaginatedAdminTasks>(`/admin/tasks?${params.toString()}`)
}

export function getQualityThresholds(): Promise<QualityThresholdsResponse> {
  return adminGet<QualityThresholdsResponse>('/quality-thresholds')
}

export function updateQualityThresholds(
  request: UpdateQualityThresholdRequest,
): Promise<QualityThresholdVersion> {
  return adminPost<QualityThresholdVersion>('/quality-thresholds', request)
}

export function listWorkers(): Promise<PptWorker[]> {
  return adminGet<PptWorker[]>('/workers')
}

export function releaseWorkerLease(workerId: string): Promise<{ released: number }> {
  return adminPost<{ released: number }>(`/workers/${workerId}/release`)
}

// ---------------------------------------------------------------------------
// Coupon (兑换码) API
// ---------------------------------------------------------------------------

export interface CouponCode {
  id: string
  code: string
  amount: number
  expiresAt?: string | null
  maxUses: number
  usedCount: number
  remark: string
  enabled: boolean
  expired: boolean
  createdBy: string
  createdAt: string
}

export interface CreateCouponRequest {
  amount: number
  expiresAt?: string
  maxUses?: number
  remark?: string
  count?: number
}

export interface CreateCouponResult {
  coupons: CouponCode[]
  createdCount: number
}

export interface PaginatedCoupons {
  list: CouponCode[]
  total: number
  page: number
  pageSize: number
}

export function createCoupons(request: CreateCouponRequest): Promise<CreateCouponResult> {
  return adminPost<CreateCouponResult>('/coupons', request)
}

export function listCoupons(page = 1, pageSize = 20, q = '', status = ''): Promise<PaginatedCoupons> {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
  if (q) params.set('q', q)
  if (status) params.set('status', status)
  return adminGet<PaginatedCoupons>(`/coupons?${params.toString()}`)
}

export function toggleCoupon(couponId: string): Promise<{ id: string; enabled: boolean }> {
  return adminPost(`/coupons/${couponId}/toggle`)
}

export function deleteCoupon(couponId: string): Promise<{ deleted: boolean }> {
  return adminRequest<{ deleted: boolean }>(`/coupons/${couponId}`, { method: 'DELETE' })
}
